import { useCallback, useMemo, useState } from 'react';
import { Card, Col, Flex, Row, Typography, message } from 'antd';
import {
  IconAffiliate,
  IconListNumbers,
  IconSettingsAutomation,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { PartnersService } from '~/app/services/partners/partners.service';
import { SlaKpiCard } from '~/app/landing/sla/shared/components/SlaKpiCard';
import { SlaPageHeader } from '~/app/landing/sla/shared/components/SlaPageHeader';
import {
  SLA_SECTION_CARD_CLASS,
  SLA_TABLE_SCROLL_X_OEM,
  SLA_TABLE_SCROLL_Y,
} from '~/app/landing/sla/shared/sla.constants';
import { useSlaPartnerTableExpandable } from '~/app/landing/sla/shared/hooks/useSlaPartnerTableExpandable';
import { buildOemRateTableRows } from '~/app/landing/sla/shared/utils/oem-rates-table.utils';
import { OemPartnerExpandedRow } from '@/app/landing/sla/oems/components/OemPartnerExpandedRow';
import { MaintenanceSlaRulesSection } from '~/app/landing/sla/oems/components/MaintenanceSlaRulesSection/MaintenanceSlaRulesSection';
import type { MaintenancePartnershipValidity } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { toPartnershipValidity } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';
import { MaintenanceSlaContractModal } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaContractModal/MaintenanceSlaContractModal';
import { OemPartnerModal } from '@/app/landing/sla/oems/OemPartnerModal/OemPartnerModal';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import {
  useOemPartnerColumns,
  useOemRatesColumns,
} from '@/app/landing/sla/oems/hooks/useOemColumns';
import { usePartnerMaintenanceRules } from '@/app/landing/sla/oems/hooks/usePartnerMaintenanceRules';

export function OemsPageContainer() {
  const { t } = useTranslation('sla');
  const { hasPermission } = usePermission();
  const { user } = useAuth();
  const canManage = hasPermission(PermissionKey.PARTNERS_OEMS_MANAGE);
  const canManageRules = hasPermission(
    PermissionKey.PARTNER_MAINTENANCE_RULES_MANAGE,
  );
  const organizationId = user?.organizationId ?? '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<OemPartnerGroupDto | null>(null);
  const maintenanceRules = usePartnerMaintenanceRules(organizationId);
  const [maintenanceEditor, setMaintenanceEditor] = useState<{
    relationshipId: string;
    partnerName: string;
    partnership: MaintenancePartnershipValidity;
  } | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['partners', 'oems', organizationId],
    queryFn: () => PartnersService.listOems(organizationId),
    enabled: !!organizationId,
  });

  const openCreate = () => {
    setEditingRow(null);
    setModalOpen(true);
  };

  const partnerColumns = useOemPartnerColumns({
    canManage,
    onEdit: (row) => {
      setEditingRow(row);
      setModalOpen(true);
    },
  });

  const ratesColumns = useOemRatesColumns();

  const ratesData = useMemo(
    () => buildOemRateTableRows(data?.items ?? []),
    [data?.items],
  );

  const openMaintenanceEditor = useCallback((row: OemPartnerGroupDto) => {
    if (!row.slaContract) {
      return;
    }
    setMaintenanceEditor({
      relationshipId: row.relationshipId,
      partnerName: row.partnerName,
      partnership: toPartnershipValidity(row.slaContract),
    });
  }, []);

  const renderPartnerExpandedRow = useCallback(
    (row: OemPartnerGroupDto) => <OemPartnerExpandedRow row={row} />,
    [],
  );

  const partnerExpandable = useSlaPartnerTableExpandable({
    renderExpandedRow: renderPartnerExpandedRow,
    rowExpandable: (row) => !!row.slaContract,
  });

  const tableLocale = useMemo(
    () => ({
      emptyText: (
        <Flex vertical align="center" gap={8} style={{ padding: '32px 0' }}>
          <Typography.Text type="secondary">{t('oems.empty')}</Typography.Text>
        </Flex>
      ),
    }),
    [t],
  );

  return (
    <AuthGuard requiredPermissions={[PermissionKey.PARTNERS_OEMS_READ]}>
      <div className="app-page app-page--sla">
        <SlaPageHeader
          title={t('oems.title')}
          subtitle={t('oems.subtitle')}
          addLabel={t('oems.add')}
          onAdd={openCreate}
          canAdd={canManage}
          icon={<IconAffiliate size={26} stroke={1.6} />}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <SlaKpiCard
              title={t('oems.kpi.partners')}
              value={data?.partnerCount ?? 0}
              icon={<IconAffiliate size={24} stroke={1.6} />}
              accentColor="#7c3aed"
            />
          </Col>
          <Col xs={24} md={12}>
            <SlaKpiCard
              title={t('oems.kpi.activeRates')}
              value={data?.activeRateCount ?? 0}
              icon={<IconListNumbers size={24} stroke={1.6} />}
              accentColor="#00a87e"
            />
          </Col>
        </Row>

        <Card
          className={SLA_SECTION_CARD_CLASS}
          title={
            <span className="ds-section-header">{t('oems.groupsTitle')}</span>
          }
          styles={{ body: { padding: 0 } }}
        >
          <EvimoTable
            className="sla-partner-table"
            rowKey="relationshipId"
            loading={isLoading}
            columns={partnerColumns}
            dataSource={data?.items ?? []}
            pagination={false}
            scroll={{ x: SLA_TABLE_SCROLL_X_OEM, y: SLA_TABLE_SCROLL_Y }}
            locale={tableLocale}
            size="middle"
            expandable={partnerExpandable}
          />
        </Card>

        <Card
          className={SLA_SECTION_CARD_CLASS}
          title={
            <Flex align="center" gap={8}>
              <IconSettingsAutomation
                size={18}
                className="sla-section-card__title-icon"
                aria-hidden
              />
              <span className="ds-section-header">{t('oems.ratesTitle')}</span>
            </Flex>
          }
          styles={{ body: { padding: 0 } }}
        >
          <EvimoTable
            rowKey="key"
            dataSource={ratesData}
            columns={ratesColumns}
            pagination={false}
            scroll={{ x: SLA_TABLE_SCROLL_X_OEM, y: SLA_TABLE_SCROLL_Y }}
            locale={tableLocale}
            size="middle"
          />
        </Card>

        <MaintenanceSlaRulesSection
          organizationId={organizationId}
          partners={data?.items ?? []}
          contracts={maintenanceRules.contracts}
          loading={isLoading || maintenanceRules.isLoading}
          canManage={canManageRules}
          locale={tableLocale}
          onEdit={openMaintenanceEditor}
        />

        <MaintenanceSlaContractModal
          open={maintenanceEditor !== null}
          organizationId={organizationId}
          relationshipId={maintenanceEditor?.relationshipId ?? ''}
          partnerName={maintenanceEditor?.partnerName ?? ''}
          partnership={maintenanceEditor?.partnership ?? null}
          contract={
            maintenanceEditor
              ? (maintenanceRules.contracts[maintenanceEditor.relationshipId] ??
                null)
              : null
          }
          onClose={() => setMaintenanceEditor(null)}
          onSave={async (record, rulesStatus) => {
            try {
              const [saved] = await Promise.all([
                maintenanceRules.save(record, rulesStatus),
                Promise.resolve(undefined),
              ]);
              if (saved) {
                setMaintenanceEditor((editor) =>
                  editor
                    ? {
                        ...editor,
                      }
                    : editor,
                );
              }
              setMaintenanceEditor(null);
            } catch (error) {
              message.error(
                error instanceof Error
                  ? error.message
                  : t('oems.maintenance.saveFailed'),
              );
              throw error;
            }
          }}
        />

        <OemPartnerModal
          open={modalOpen}
          organizationId={organizationId}
          editingRow={editingRow}
          onClose={() => {
            setModalOpen(false);
            setEditingRow(null);
          }}
          onSuccess={() => {
            setModalOpen(false);
            setEditingRow(null);
            void refetch();
          }}
        />
      </div>
    </AuthGuard>
  );
}
